import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { IS_DESKTOP } from "../config";
import { activateLicense, useLicenseStatus } from "../services/licenseClient";

const errorMessage = (reason: string): string => {
  switch (reason) {
    case "expired":
      return "That license key has expired.";
    case "bad-format":
    case "bad-payload":
    case "bad-signature":
    case "verify-failed":
      return "That doesn't look like a valid license key.";
    default:
      return "Could not activate that key.";
  }
};

/**
 * Gates a tool panel behind a license key, except the viewer (which never
 * renders through here, so it's always free) and any panel passed
 * `bypass` — the free-tier tools decided in .md/licensing-proposal.md
 * (Rotate, Reorder, and the rest of the viewer-adjacent editing tools).
 * Web builds aren't gated yet — paid licensing ships on desktop first.
 */
const LicenseGate: React.FC<{ children: React.ReactNode; bypass?: boolean }> = ({
  children,
  bypass,
}) => {
  const status = useLicenseStatus();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!IS_DESKTOP || bypass || status.valid) return <>{children}</>;

  const submit = async () => {
    const trimmed = key.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    const result = await activateLicense(trimmed);
    setBusy(false);
    if (!result.valid) setError(errorMessage(result.reason));
  };

  return (
    <>
      <p className="tool-hint">
        This tool requires a license. The viewer always stays free.
      </p>
      {status.reason === "expired" && (
        <p className="tool-result">Your previous license key has expired.</p>
      )}
      <input
        className="tool-input"
        placeholder="Paste your license key"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
      />
      <button className="tool-primary" disabled={busy || !key.trim()} onClick={submit}>
        {busy ? <Loader2 size={16} className="spin" /> : null} Activate
      </button>
      {error && <p className="tool-result">{error}</p>}
    </>
  );
};

export default LicenseGate;
